import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const SKIP_VALUE = '__skip__';
const STEPS = ['Select ad accounts', 'Map Facebook pages'];

const FacebookAccountSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingPages, setSavingPages] = useState(false);
  const [error, setError] = useState('');

  // Two-step wizard: 0 = ad-account selection, 1 = page → brand mapping.
  const [stepIndex, setStepIndex] = useState(0);

  // Data from OAuth
  const [userInfo, setUserInfo] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [brandId, setBrandId] = useState(null);
  const [tempId, setTempId] = useState(null);

  // Multi-brand support
  const [brands, setBrands] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountBrandMapping, setAccountBrandMapping] = useState({});

  // Page-step state. `pageBrandMapping[page_id]` is either a numeric brand id or SKIP_VALUE.
  // `selectedPageIds` is the multi-select set used by the bulk-action toolbar.
  const [pageBrandMapping, setPageBrandMapping] = useState({});
  const [selectedPageIds, setSelectedPageIds] = useState(new Set());
  const [bulkBrand, setBulkBrand] = useState('');

  // Scope-reduction warning. If this OAuth's user has previously granted pages
  // that are NOT in the new grant, completing onboarding will revoke those tokens
  // for OTHER connectors. The user must explicitly acknowledge before proceeding.
  const [scopeWarning, setScopeWarning] = useState(null);
  const [scopeAcknowledged, setScopeAcknowledged] = useState(false);

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }

    setTempId(temp_id);
    loadFacebookAccounts(temp_id);
    fetchBrands();
  }, [searchParams]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const loadFacebookAccounts = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/facebook/accounts', {
        params: { temp_id }
      });

      if (response.data.success) {
        setUserInfo(response.data.user_info);
        setAdAccounts(response.data.ad_accounts);
        setBrandId(response.data.brand_id);
        setPages(response.data.pages || []);
        setScopeWarning(response.data.scope_warning || null);
        // Default every page to "Skip" — safer than auto-attaching to the connector's brand,
        // which is exactly the silent-misattribution bug this step is closing.
        const defaultMap = {};
        (response.data.pages || []).forEach(p => {
          defaultMap[p.page_id] = SKIP_VALUE;
        });
        setPageBrandMapping(defaultMap);
      } else {
        setError(response.data.message || 'Failed to load Meta accounts');
      }
    } catch (error) {
      console.error('Error loading Facebook accounts:', error);
      setError(error.response?.data?.message || 'Failed to load Meta accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelection = (accountId, checked) => {
    if (checked) {
      const account = adAccounts.find(a => a.account_id === accountId);
      setSelectedAccounts([...selectedAccounts, account]);
      // For already-connected accounts, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth flow. Kept numeric: the brand <MenuItem>
      // values here are numeric brand ids, and MUI Select matches by strict equality.
      const defaultBrand = account?.alreadyConnected && account?.connectedBrandId
        ? Number(account.connectedBrandId)
        : brandId;
      setAccountBrandMapping(prev => ({
        ...prev,
        [accountId]: defaultBrand
      }));
    } else {
      setSelectedAccounts(selectedAccounts.filter(a => a.account_id !== accountId));
      setAccountBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[accountId];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (accountId, newBrandId) => {
    setAccountBrandMapping(prev => ({
      ...prev,
      [accountId]: newBrandId
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedAccounts.length === 0) {
      setError('Please select at least one Meta ad account');
      return;
    }

    const accountsWithoutBrands = selectedAccounts.filter(account =>
      !accountBrandMapping[account.account_id]
    );
    if (accountsWithoutBrands.length > 0) {
      setError(`Please select a brand for all selected accounts`);
      return;
    }

    setCreating(true);
    setError('');

    try {
      for (const account of selectedAccounts) {
        const selectedBrandId = accountBrandMapping[account.account_id] || brandId;
        const selectedBrand = brands.find(b => b.id === parseInt(selectedBrandId));
        const brandName = selectedBrand ? selectedBrand.name : 'Unknown Brand';
        const connectorName = `Meta Ads - ${brandName} - ${account.name}`;

        const response = await api.post('/api/connectors/facebook/create', {
          temp_id: tempId,
          account_id: account.account_id,
          account_name: account.name,
          connector_name: connectorName,
          brand_id: parseInt(selectedBrandId)
        });

        if (!response.data.success) {
          throw new Error(`Failed to create connector for ${account.name}: ${response.data.message}`);
        }
      }

      // Connectors created. If pages exist, advance to step 2; otherwise finish.
      if (pages.length === 0) {
        await finalizeAndNavigate(0);
        return;
      }
      setStepIndex(1);
    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  // Best-effort cleanup of OAuth temp data + navigation back to /connectors with toast.
  const finalizeAndNavigate = async (mappedPageCount) => {
    try {
      await api.post('/api/connectors/facebook/cleanup', { temp_id: tempId });
    } catch (cleanupError) {
      console.warn('Failed to cleanup temp data:', cleanupError);
    }

    const uniqueBrands = [...new Set(selectedAccounts.map(account =>
      getBrandName(accountBrandMapping[account.account_id] || brandId)
    ))];

    const pageMsg = mappedPageCount > 0
      ? `, mapped ${mappedPageCount} Facebook page(s)`
      : (pages.length > 0 ? `, all pages skipped` : '');

    navigate('/connectors', {
      state: {
        message: `Successfully created ${selectedAccounts.length} Meta connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}${pageMsg}`,
        severity: 'success'
      }
    });
  };

  const handlePageBrandChange = (pageId, value) => {
    setPageBrandMapping(prev => ({ ...prev, [pageId]: value }));
  };

  const handlePageCheckbox = (pageId, checked) => {
    setSelectedPageIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(pageId); else next.delete(pageId);
      return next;
    });
  };

  const handleSelectAllPages = (checked) => {
    if (checked) {
      setSelectedPageIds(new Set(pages.map(p => p.page_id)));
    } else {
      setSelectedPageIds(new Set());
    }
  };

  const applyBulkAssignment = (value) => {
    if (selectedPageIds.size === 0) return;
    setPageBrandMapping(prev => {
      const next = { ...prev };
      selectedPageIds.forEach(pid => { next[pid] = value; });
      return next;
    });
  };

  const handleSavePageMappings = async () => {
    setSavingPages(true);
    setError('');

    try {
      const mappings = pages
        .filter(p => {
          const v = pageBrandMapping[p.page_id];
          return v && v !== SKIP_VALUE;
        })
        .map(p => ({
          page_id: p.page_id,
          page_name: p.page_name,
          brand_id: parseInt(pageBrandMapping[p.page_id]),
          ig_user_id: p.ig_user_id || ''
        }));

      if (mappings.length > 0) {
        const response = await api.post('/api/connectors/facebook/pages/map', {
          temp_id: tempId,
          mappings
        });
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to save page mappings');
        }
      }

      await finalizeAndNavigate(mappings.length);
    } catch (err) {
      console.error('Error saving page mappings:', err);
      setError(err.message || 'Failed to save page mappings');
    } finally {
      setSavingPages(false);
    }
  };

  const getBrandName = (id) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(id));
    return brand ? brand.name : 'Unknown Brand';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'CLOSED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading Meta ad accounts...</Typography>
      </Box>
    );
  }

  const mappedCount = Object.values(pageBrandMapping).filter(v => v && v !== SKIP_VALUE).length;
  const skippedCount = pages.length - mappedCount;

  const renderAdAccountsStep = () => (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Meta Ad Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose one or more Meta ad accounts to connect. You can assign each account to a different brand.
          </Typography>

          <Grid container spacing={2}>
            {adAccounts.map((account) => (
              <Grid item xs={12} md={6} key={account.account_id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAccounts.some(a => a.account_id === account.account_id)}
                          onChange={(e) => handleAccountSelection(account.account_id, e.target.checked)}
                          disabled={creating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#1877F2', mr: 2 }}>
                              <FacebookIcon />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {account.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {account.account_id}
                              </Typography>
                              {account.alreadyConnected && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={account.connectedBrandName
                                      ? `Connected · ${account.connectedBrandName}`
                                      : 'Already connected'}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={account.account_status || 'ACTIVE'}
                              color={getStatusColor(account.account_status)}
                            />
                            {account.currency && (
                              <Typography variant="caption" color="text.secondary">
                                {account.currency}
                              </Typography>
                            )}
                            {account.timezone_name && (
                              <Typography variant="caption" color="text.secondary">
                                • {account.timezone_name}
                              </Typography>
                            )}
                          </Box>

                          {selectedAccounts.some(a => a.account_id === account.account_id) && (
                            <Box sx={{ mt: 2 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`brand-select-${account.account_id}`}>Assign to Brand</InputLabel>
                                <Select
                                  labelId={`brand-select-${account.account_id}`}
                                  value={accountBrandMapping[account.account_id] || ''}
                                  label="Assign to Brand"
                                  onChange={(e) => handleBrandChange(account.account_id, e.target.value)}
                                  disabled={creating}
                                  sx={{ borderRadius: 1 }}
                                >
                                  {brands.map(brand => (
                                    <MenuItem key={brand.id} value={brand.id}>
                                      {brand.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {adAccounts.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedAccounts.length} account(s) selected
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/connectors')} disabled={creating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateConnectors}
              disabled={selectedAccounts.length === 0 || creating}
              startIcon={creating ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{ backgroundColor: '#1877F2', '&:hover': { backgroundColor: '#166FE5' } }}
            >
              {creating
                ? 'Creating...'
                : `Create ${selectedAccounts.length} Connector(s)${pages.length > 0 ? ' → Next' : ''}`}
            </Button>
          </Box>
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2"><strong>What happens next?</strong></Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          • Your Meta ad accounts will be connected securely<br />
          {pages.length > 0
            ? `• You'll then assign each of your ${pages.length} Facebook Page(s) to a brand (or skip pages that don't belong here)`
            : '• No Facebook Pages were found for this user — page-level data will not be tracked'}<br />
          • Data sync will be configured automatically
        </Typography>
      </Alert>
    </>
  );

  const renderPagesStep = () => {
    const allChecked = pages.length > 0 && selectedPageIds.size === pages.length;
    const someChecked = selectedPageIds.size > 0 && selectedPageIds.size < pages.length;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Map Facebook Pages to Brands
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your Meta account manages {pages.length} Page(s). Assign each Page to one of your brands —
            or choose <strong>Skip</strong> for Pages that don't belong to this company. Skipped Pages won't
            be ingested. You can change these assignments later.
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            Each Facebook Page can belong to only one brand. Pages set to <strong>Skip</strong> are ignored
            entirely — fetchers won't pull insights, posts, or comments for them.
          </Alert>

          {/* Bulk-action toolbar */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: 'rgba(24, 119, 242, 0.04)',
              borderRadius: 1
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selectedPageIds.size} of {pages.length} selected
            </Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="bulk-brand-label">Bulk assign to brand</InputLabel>
              <Select
                labelId="bulk-brand-label"
                label="Bulk assign to brand"
                value={bulkBrand}
                onChange={(e) => setBulkBrand(e.target.value)}
                disabled={savingPages || selectedPageIds.size === 0}
              >
                {brands.map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              disabled={!bulkBrand || selectedPageIds.size === 0 || savingPages}
              onClick={() => applyBulkAssignment(bulkBrand)}
            >
              Apply to selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={selectedPageIds.size === 0 || savingPages}
              onClick={() => applyBulkAssignment(SKIP_VALUE)}
            >
              Mark selected as Skip
            </Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allChecked}
                      indeterminate={someChecked}
                      onChange={(e) => handleSelectAllPages(e.target.checked)}
                      disabled={savingPages}
                    />
                  </TableCell>
                  <TableCell>Page</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Instagram</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Assign to brand</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pages.map((page) => {
                  const value = pageBrandMapping[page.page_id] ?? SKIP_VALUE;
                  const isSkipped = value === SKIP_VALUE;
                  return (
                    <TableRow
                      key={page.page_id}
                      hover
                      sx={{ opacity: isSkipped ? 0.55 : 1 }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPageIds.has(page.page_id)}
                          onChange={(e) => handlePageCheckbox(page.page_id, e.target.checked)}
                          disabled={savingPages}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {page.page_name || '(no name)'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {page.page_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {page.category || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {page.ig_user_id ? (
                          <Tooltip title={`IG user id: ${page.ig_user_id}`}>
                            <Chip
                              size="small"
                              icon={<InstagramIcon fontSize="small" />}
                              label="linked"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={value}
                            onChange={(e) => handlePageBrandChange(page.page_id, e.target.value)}
                            disabled={savingPages}
                          >
                            <MenuItem value={SKIP_VALUE}>
                              <em>Skip — not part of this company</em>
                            </MenuItem>
                            {brands.map(brand => (
                              <MenuItem key={brand.id} value={brand.id}>
                                {brand.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {mappedCount} Page(s) mapped · {skippedCount} skipped
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleSavePageMappings}
                disabled={savingPages}
              >
                Skip all & finish
              </Button>
              <Button
                variant="contained"
                onClick={handleSavePageMappings}
                disabled={savingPages}
                startIcon={savingPages ? <CircularProgress size={20} /> : <CheckIcon />}
                sx={{ backgroundColor: '#1877F2', '&:hover': { backgroundColor: '#166FE5' } }}
              >
                {savingPages ? 'Saving...' : 'Save Page Mappings & Finish'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ maxWidth: stepIndex === 0 ? 800 : 1100, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/connectors')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <FacebookIcon sx={{ color: '#1877F2', mr: 2, fontSize: 32 }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Meta Setup
          </Typography>
          {userInfo && (
            <Typography variant="body2" color="text.secondary">
              Authenticated as {userInfo.name} ({userInfo.email})
            </Typography>
          )}
        </Box>
      </Box>

      <Stepper activeStep={stepIndex} sx={{ mb: 4 }}>
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {scopeWarning && !scopeAcknowledged && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            ⚠️ Meta scope reduction detected
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This Meta account (<strong>{scopeWarning.user_name || scopeWarning.user_id}</strong>) previously
            granted access to <strong>{scopeWarning.total_lost_pages} page(s)</strong> that are <strong>NOT</strong> in
            this new grant. Meta tracks page access at the user level, so continuing will revoke
            those page tokens and break <strong>{scopeWarning.affected_connectors.length} existing connector(s)</strong>.
          </Typography>
          <Box sx={{ pl: 1, mb: 2 }}>
            {scopeWarning.affected_connectors.map(c => (
              <Box key={c.connector_id} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {c.connector_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pages that will lose access: {c.lost_pages.map(p => p.page_name || p.page_id).join(', ')}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Recommended:</strong> Cancel, reconnect, and in Meta's page
            selector dialog make sure to keep ALL previously-connected pages selected (you can add new
            ones too).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/connectors')}
            >
              Cancel & re-authenticate
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setScopeAcknowledged(true)}
            >
              I understand, proceed anyway
            </Button>
          </Box>
        </Alert>
      )}

      {(!scopeWarning || scopeAcknowledged) && (stepIndex === 0 ? renderAdAccountsStep() : renderPagesStep())}
    </Box>
  );
};

export default FacebookAccountSelection;
